import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 font-sans font-medium transition-all duration-normal ease-smooth select-none disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-white hover:bg-accent-hover hover:shadow-glow',
        secondary: 'bg-bg-input border border-border text-text-primary hover:bg-bg-hover hover:border-border-focus',
        ghost: 'bg-transparent text-text-secondary hover:bg-bg-hover hover:text-text-primary',
        violet: 'bg-violet-muted border border-violet text-violet hover:bg-[rgba(167,139,250,0.2)]',
        danger: 'bg-error-muted border border-error text-error hover:bg-[rgba(217,107,107,0.2)]',
        success: 'bg-success-muted border border-success text-success hover:bg-[rgba(141,185,139,0.2)]',
      },
      size: {
        default: 'px-[14px] py-[7px] text-sm rounded-md',
        sm:      'px-[10px] py-[5px] text-xs rounded-md',
        icon:    'w-8 h-8 p-0 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
)
Button.displayName = 'Button'

export { Button, buttonVariants }
